/**
 * Preferinte locale: tema si densitatea. Ambele se scriu ca atribute pe `<html>` (`data-theme`,
 * `data-density`), pentru ca sistemul de tokeni sa poata fi pur CSS — comutarea de tema nu are
 * voie sa treaca prin re-randari de React.
 *
 * Alegerea explicita bate OS-ul (garda `:not([data-theme=...])` din `tokens.css`), iar `sistem`
 * inseamna ca atributul e SCOS, nu setat pe o valoare.
 */

import { useCallback, useEffect, useState } from 'react';

export type Tema = 'light' | 'dark' | 'sistem';
export type Densitate = 'compact' | 'standard' | 'relaxat';

const CHEIE_TEMA = 'evomag.tema';
const CHEIE_DENSITATE = 'evomag.densitate';

export const ETICHETE_TEMA: Record<Tema, string> = {
  light: 'Clar',
  dark: 'Închis',
  sistem: 'Sistem',
};

export const ETICHETE_DENSITATE: Record<Densitate, string> = {
  compact: 'Compact',
  standard: 'Standard',
  relaxat: 'Relaxat',
};

/** Inaltimea de rand a fiecarei densitati, afisata in comutator ca sa fie o alegere informata. */
export const INALTIMI_DENSITATE: Record<Densitate, string> = {
  compact: '28px',
  standard: '32px',
  relaxat: '40px',
};

function citeste<T extends string>(cheie: string, valide: readonly T[], implicit: T): T {
  try {
    const brut = localStorage.getItem(cheie);
    return valide.includes(brut as T) ? (brut as T) : implicit;
  } catch {
    return implicit;
  }
}

function scrie(cheie: string, valoare: string): void {
  try {
    localStorage.setItem(cheie, valoare);
  } catch {
    /* modul privat: preferinta rmne valabila doar pentru sesiunea curenta */
  }
}

export function useTema(): [Tema, (t: Tema) => void] {
  const [tema, setTema] = useState<Tema>(() =>
    citeste(CHEIE_TEMA, ['light', 'dark', 'sistem'] as const, 'sistem'),
  );

  useEffect(() => {
    const root = document.documentElement;
    if (tema === 'sistem') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', tema);
  }, [tema]);

  const schimba = useCallback((t: Tema) => {
    setTema(t);
    scrie(CHEIE_TEMA, t);
  }, []);

  return [tema, schimba];
}

export function useDensitate(): [Densitate, (d: Densitate) => void] {
  const [densitate, setDensitate] = useState<Densitate>(() =>
    citeste(CHEIE_DENSITATE, ['compact', 'standard', 'relaxat'] as const, 'standard'),
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-density', densitate);
  }, [densitate]);

  const schimba = useCallback((d: Densitate) => {
    setDensitate(d);
    scrie(CHEIE_DENSITATE, d);
  }, []);

  return [densitate, schimba];
}
